import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import type { SummaryState, SummaryItem, InitElementsParams } from '../type/ai-search.type';
import { AISearchSummaryModel } from '../model/ai-search-summary.model';
import type { AISearchRepositorySummaryData } from './ai-search.repository.interface';
import _ from 'lodash';
import { AISearchRepository } from './ai-search.repository.interface';

export const AISearchSummaryService = Symbol('AISearchSummaryService');

export interface AISearchSummaryService {
    getModelData: () => SummaryState;
    getSummaryDesc: (idx: number) => string;
    getTopic: () => string;
    readonly onStatesChange: Event<SummaryState>;
    readonly onElementsGet: Event<InitElementsParams>;

    initSumaryState: (getSummary: () => Promise<AISearchRepositorySummaryData>) => Promise<void>;
    handleSeleteSummary: (item: SummaryItem) => void;
    handleChangeRelatedIpc: (relatedIpc: SummaryItem['relatedIpc']) => void;
    handleChangeSummaryIpc: (summaryIdx: number, relatedIpc: SummaryItem['relatedIpc']) => void;
    handleGetElements: () => void;
}

@injectable()
export class AISearchSummaryServiceImpl implements AISearchSummaryService {
    private _onStatesChangeEmitter = new Emitter<SummaryState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    private _onElementsGetEmitter = new Emitter<InitElementsParams>();
    readonly onElementsGet = this._onElementsGetEmitter.event;

    constructor(
        @inject(AISearchSummaryModel)
        private _model: AISearchSummaryModel,
        @inject(AISearchRepository)
        private _repository: AISearchRepository,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }
    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    private _setModelData(value: Partial<SummaryState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public getModelData(): SummaryState {
        return this._model.modelData;
    }

    public getSummaryDesc(idx: number): string {
        return this._model.modelData.summary[idx]?.desc ?? '';
    }

    public getTopic(): string {
        return this._model.modelData.topic;
    }

    public async initSumaryState(getSummary: () => Promise<AISearchRepositorySummaryData>) {
        this._setModelData({ loading: true });
        const result = await getSummary();
        this._setModelData({
            ...result,
            loading: false,
            summarySelected: _.cloneDeep(result.summary),
            summary: result.summary.map(summary => (
                { ...summary, ipcLoading: false }
            )),
        });
    }

    public handleSeleteSummary(item: SummaryItem) {
        const state = this.getModelData();
        const summarySelected = state.summarySelected;

        const feature = item.feature;
        const index = summarySelected.findIndex(seleted => seleted.feature === feature);
        if (index === -1) {
            summarySelected.push(item);
        } else {
            summarySelected.splice(index, 1);
        }
        this._setModelData({
            summarySelected: state.summarySelected,
        });
    }

    public handleChangeRelatedIpc(relatedIpc: SummaryItem['relatedIpc']) {
        this._setModelData({
            relatedIpc,
        });
    }

    public handleChangeSummaryIpc(summaryIdx: number, relatedIpc: SummaryItem['relatedIpc']) {
        const state = this.getModelData();
        state.summary[summaryIdx].relatedIpc = relatedIpc;
        this._setModelData({
            summary: state.summary,
        });
    }

    public handleChangeTopic(topic: string) {
        this._setModelData({
            topic,
            ipcLoading: true,
        });

        this._repository.getRelatedIpc(topic, 'AREA').then(res => {
            this.handleChangeRelatedIpc(res);
            this._setModelData({
                ipcLoading: false,
            });
        });
    }

    private _updateSummary(idx: number, newSummary: Partial<SummaryItem & { ipcLoading: boolean }>) {
        const state = this.getModelData();
        state.summary[idx] = { ...state.summary[idx], ...newSummary };
        this._setModelData({
            summary: state.summary,
        });
    }

    public handleChangeSummary(summaryIdx: number, desc: string) {
        this._updateSummary(summaryIdx, {
            desc,
            ipcLoading: true,
        });

        this._repository.getRelatedIpc(desc, 'TOPIC').then(res => {
            this.handleChangeSummaryIpc(summaryIdx, res);

            this._updateSummary(summaryIdx, {
                ipcLoading: false,
            });
        });
    }

    public handleGetElements() {
        const state = this.getModelData();
        this._onElementsGetEmitter.fire({
            summary: state.summarySelected,
            topic: state.topic,
            relatedIpc: state.relatedIpc,
        });
    }
}